import React, { useState } from "react";
import { Link } from "react-router-dom";
import "../../../css/partner/LeadDetailView.css";
import ToolSidebar from "../../utils/ToolSideBar";
import { features } from "../../utils/features";

const BASE_TABS = ["Overview", "Intake", "Messages"];

function hasValue(value) {
  return value !== undefined && value !== null && value !== "" && value !== "Not provided";
}

function date(value) {
  return value ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "-";
}

function joinNames(items) {
  return (items || []).map((item) => item.name).filter(Boolean).join(", ");
}

function safeUrl(value) {
  if (!hasValue(value)) return "";
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

function DetailField({ label, value, href }) {
  if (!hasValue(value)) return null;
  return <div className="lead-detail-field"><span>{label}</span>{href ? <a href={href} target="_blank" rel="noopener noreferrer">{value}</a> : <strong>{value}</strong>}</div>;
}

function EmptyState({ children }) {
  return <div className="lead-detail-empty">{children}</div>;
}

function FeatureList({ items }) {
  if (!items?.length) return <EmptyState>No requested features are listed.</EmptyState>;
  return (
    <div className="lead-chip-list">
      {items.map((item) => <span key={item.id || item.name}>{item.name}</span>)}
    </div>
  );
}

function OverviewTab({ lead }) {
  const website = safeUrl(lead.current_website);
  const social = safeUrl(lead.social_media);
  return (
    <div className="lead-detail-grid-layout">
      <section className="lead-detail-card">
        <div className="lead-card-header">
          <h2>Contact Information</h2>
          <div className="lead-actions">
            {lead.conversation_id && <Link to={`/messages/${lead.conversation_id}`}>Message Lead</Link>}
          </div>
        </div>
        <div className="lead-field-grid">
          <DetailField label="Name" value={lead.full_name || lead.name} />
          <DetailField label="Email" value={lead.email} href={lead.email ? `mailto:${lead.email}` : ""} />
          <DetailField label="Phone" value={lead.phone} />
          <DetailField label="Preferred Contact" value={lead.preferred_contact_method || "Email"} />
        </div>
      </section>

      <section className="lead-detail-card">
        <div className="lead-card-header"><h2>Review Status</h2></div>
        <div className="lead-field-grid">
          <DetailField label="Current Status" value={lead.review_status_label || lead.status_label} />
          <DetailField label="Submitted" value={date(lead.created_at)} />
          <DetailField label="Last Updated" value={date(lead.updated_at)} />
          <DetailField label="Needs Information" value={lead.review_status === "more_information_needed" ? "Yes" : "No"} />
        </div>
      </section>

      <section className="lead-detail-card">
        <div className="lead-card-header"><h2>Business Information</h2></div>
        <div className="lead-field-grid">
          <DetailField label="Business Name" value={lead.business_name || lead.business} />
          <DetailField label="Industry" value={lead.industry} />
          <DetailField label="Business Stage" value={lead.business_stage} />
          <DetailField label="Current Website" value={lead.current_website} href={website} />
          <DetailField label="Social Media" value={lead.social_media} href={social} />
        </div>
      </section>

      <section className="lead-detail-card wide">
        <div className="lead-card-header"><h2>Request Summary</h2></div>
        <div className="lead-field-grid">
          <DetailField label="Requested Project Type(s)" value={joinNames(lead.project_types)} />
          <DetailField label="Budget" value={lead.budget} />
          <DetailField label="Timeline" value={lead.timeline} />
          <DetailField label="Content Status" value={lead.content_status} />
        </div>
        <div className="lead-summary-copy">
          {hasValue(lead.project_goal) && <p><strong>Goal</strong>{lead.project_goal}</p>}
          {hasValue(lead.project_description) && <p><strong>Description</strong>{lead.project_description}</p>}
        </div>
        <FeatureList items={lead.features} />
      </section>
    </div>
  );
}

function IntakeTab({ lead }) {
  const sections = lead.intake_sections || [];
  if (!sections.length) return <EmptyState>No intake answers are available for this lead.</EmptyState>;
  return (
    <div className="lead-intake-list">
      {sections.map((section) => (
        <section className="lead-detail-card" key={section.title}>
          <div className="lead-card-header"><h2>{section.title}</h2></div>
          <div className="lead-field-grid">
            {(section.items || []).map((item) => <DetailField key={`${section.title}-${item.label}`} label={item.label} value={item.value} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

function MessagesTab({ lead }) {
  if (!lead.conversation_id) return <EmptyState>No conversation exists for this lead yet.</EmptyState>;
  return <Link className="lead-open-link" to={`/messages/${lead.conversation_id}`}>Open conversation with {lead.full_name || lead.name}</Link>;
}

function MeetingsTab({ lead }) {
  return (
    <div className="lead-intake-list">
      <EmptyState>Use the meeting scheduler to create or view meetings for this lead.</EmptyState>
      <Link className="lead-open-link align-start" to="/meetings/new">Schedule Meeting</Link>
    </div>
  );
}

export default function LeadDetailView({ lead }) {
  const [activeTab, setActiveTab] = useState("Overview");
  if (!lead) return <div className="lead-detail-loading">Loading...</div>;
  const title = lead.full_name || lead.name || lead.email || `Lead #${lead.id}`;
  const tabs = features.meetingsEnabled ? [...BASE_TABS, "Meetings"] : BASE_TABS;

  return (
    <div className="lead-detail-shell">
      <ToolSidebar active="leads" />
      <main className="lead-detail-page">
        <div className="lead-detail-container">
          <header className="lead-detail-hero">
            <div>
              <span>LEAD INTAKE</span>
              <h1>{title}</h1>
              <p>{lead.business_name || lead.business || "Business not provided"}</p>
            </div>
            <div className="lead-hero-meta">
              <span className={`lead-status ${lead.review_status}`}>{lead.review_status_label || lead.status_label || "Pending Review"}</span>
              <strong>Submitted {date(lead.created_at)}</strong>
            </div>
          </header>

          <nav className="lead-tabs" role="tablist" aria-label="Lead detail sections">
            {tabs.map((tab) => <button key={tab} type="button" role="tab" aria-selected={activeTab === tab} className={activeTab === tab ? "active" : ""} onClick={() => setActiveTab(tab)}>{tab}</button>)}
          </nav>

          {activeTab === "Overview" && <OverviewTab lead={lead} />}
          {activeTab === "Intake" && <IntakeTab lead={lead} />}
          {activeTab === "Messages" && <MessagesTab lead={lead} />}
          {features.meetingsEnabled && activeTab === "Meetings" && <MeetingsTab lead={lead} />}
        </div>
      </main>
    </div>
  );
}






